/**
 * ProfileShareButton — Copy-to-clipboard share button for a user's profile URL.
 *
 * Renders a ghost-styled button that belongs inside the gradient header
 * action row on both the public profile view (/profile/:uid) and the
 * profile settings view (/profile/edit). Clicking copies
 * `${origin}/profile/${uid}` to the clipboard; the icon swaps to a
 * checkmark for ~2s on success, and a toast is dispatched on failure.
 *
 * The component is self-contained (owns dispatch, copy, feedback, toast).
 * Each call site supplies the uid plus its own translated strings so
 * there's no lang-file coupling between the two pages.
 *
 * @shared CommunityProfilePage, CommunityProfileSettingsPage
 */

import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import ShareAndroidSolid from '../../../../Icon/icons/ShareAndroidSolid/index'
import ShareAndroid from '../../../../Icon/icons/ShareAndroid/index'
import Check from '../../../../Icon/icons/Check/index'
import { addToasty } from '../../../../../services/toasty/actions'
import useCopyFeedback from '../../../../../services/community/shared/useCopyFeedback'

const DEFAULT_LABEL = 'Share'
const DEFAULT_ERROR_MESSAGE = 'Unable to copy profile link.'

export interface ProfileShareButtonProps {
  /** The uid of the profile to share — used to build the `/profile/{uid}` URL */
  uid: string
  /** Visible button label. Defaults to 'Share' — parent should pass the
   *  translated string from its own staticText. Suppressed when
   *  `iconOnly` is true. */
  label?: string
  /** Toast message shown on clipboard failure. Defaults to English. */
  errorMessage?: string
  /** Optional extra classnames appended alongside the shared action-btn
   *  classes (`profile-gradient-header__action-btn` and `__share-btn`). */
  className?: string
  /** When true, render only the icon (no text label) and append a
   *  `--icon-only` modifier class. Used in tile-density surfaces
   *  (e.g. ProfileTileCard) where the gradient-header label width
   *  would crowd the card. The label is still passed via `aria-label`
   *  for screen-reader accessibility. */
  iconOnly?: boolean
  /** Fires after a successful copy-to-clipboard. The parent owns analytics
   *  so this presentational component doesn't need a PostHog hook
   *  (transitive Redux dependency complicates unit tests). The argument is
   *  `'copy_link'` today — when native share is added (see
   *  `PlaylistSharePreview` for the pattern) this widens to
   *  `'copy_link' | 'native_share'`. */
  onShared?: (method: 'copy_link') => void
}

function ProfileShareButton ({
  uid,
  label = DEFAULT_LABEL,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  className,
  iconOnly = false,
  onShared,
}: ProfileShareButtonProps) {
  const dispatch = useDispatch()

  const onError = useCallback(() => {
    dispatch(addToasty(errorMessage))
  }, [dispatch, errorMessage])

  const { copied, handleCopy } = useCopyFeedback({ onError })

  const handleClick = useCallback(() => {
    if (!uid || typeof window === 'undefined') return
    handleCopy(`${window.location.origin}/profile/${uid}`)
    if (onShared) onShared('copy_link')
  }, [uid, handleCopy, onShared])

  // Icon-only mode mirrors `PlaylistShareButton`: borderless Link-style
  // button sized to ~36px square, neutral color hover → teal. Used in
  // tile-density surfaces (ProfileTileCard) where the gradient-header's
  // border + label would crowd the card.
  // Default mode keeps the gradient-header chain so existing call sites
  // (CommunityProfilePage, CommunityProfileSettingsPage) are unchanged.
  if (iconOnly) {
    // Match `community-playlist-row-header__actions` share button:
    // outlined `ShareAndroid` icon at 24px, borderless link-style button.
    const iconClasses = [
      'profile-share-button',
      className,
    ].filter(Boolean).join(' ')
    return (
      <Button
        type={BUTTON_TYPES.LINK}
        className={iconClasses}
        onClick={handleClick}
        aria-label={label}
      >
        {copied ? <Check size="24px" /> : <ShareAndroid size="24px" />}
      </Button>
    )
  }

  const classes = [
    'profile-gradient-header__action-btn',
    'profile-gradient-header__share-btn',
    className,
  ].filter(Boolean).join(' ')

  return (
    <Button
      type={BUTTON_TYPES.GHOST}
      className={classes}
      onClick={handleClick}
    >
      {copied ? <Check size="16px" /> : <ShareAndroidSolid size="16px" />}
      {label}
    </Button>
  )
}

export default React.memo(ProfileShareButton)
